// ============================================================================
// USER ROLES AND PERMISSIONS
// ============================================================================
export const USER_ROLES = {
  ADMIN: "admin",
  CLIENT: "client",
  LIVREUR: "livreur",
  MANAGER: "manager",
  CLIENT_SUPPORT: "clientSupport",
  ACCOUNTANT: "accountant",
};

export const USER_ROLE_COLORS = {
  admin: "border-destructive/30 shadow-destructive/30",
  client: "border-chart-1/30 shadow-chart-1/30",
  livreur: "border-chart-2/30 shadow-chart-2/30",
  manager: "border-chart-4/30 shadow-chart-4/30",
  clientSupport: "border-chart-3/30 shadow-chart-3/30",
  accountant: "border-indigo-500/30 shadow-indigo-500/30",
};

// ============================================================================
// TICKET SYSTEM CONFIGURATIONS
// ============================================================================
export const TICKET_CATEGORIES = {
  pickup_issue: "pickup_issue",
  delivery_issue: "delivery_issue",
  package_damage: "package_damage",
  payment_issue: "payment_issue",
  system_issue: "system_issue",
  general_inquiry: "general_inquiry",
  complaint: "complaint",
  feature_request: "feature_request",
  other: "other",
};

export const TICKET_PRIORITIES = {
  low: "low",
  medium: "medium",
  high: "high",
  urgent: "urgent",
};

export const TICKET_STATUSES = {
  open: "open",
  in_progress: "in_progress",
  waiting_for_response: "waiting_for_response",
  resolved: "resolved",
  closed: "closed",
};

export const TICKET_STATUS_COLORS = {
  open: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-yellow-100 text-yellow-800 border-yellow-200",
  waiting_for_response: "bg-orange-100 text-orange-800 border-orange-200",
  resolved: "bg-green-100 text-green-800 border-green-200",
  closed: "bg-gray-100 text-gray-800 border-gray-200",
};

export const TICKET_PRIORITY_COLORS = {
  low: "bg-gray-100 text-gray-800 border-gray-200",
  medium: "bg-blue-100 text-blue-800 border-blue-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  urgent: "bg-red-100 text-red-800 border-red-200",
};

export const TICKET_CATEGORY_COLORS = {
  pickup_issue: "bg-yellow-100 text-yellow-800 border-yellow-200",
  delivery_issue: "bg-orange-100 text-orange-800 border-orange-200",
  package_damage: "bg-red-100 text-red-800 border-red-200",
  payment_issue: "bg-purple-100 text-purple-800 border-purple-200",
  system_issue: "bg-gray-100 text-gray-800 border-gray-200",
  general_inquiry: "bg-blue-100 text-blue-800 border-blue-200",
  complaint: "bg-red-100 text-red-800 border-red-200",
  feature_request: "bg-green-100 text-green-800 border-green-200",
  other: "bg-gray-100 text-gray-800 border-gray-200",
};

// ============================================================================
// PACKAGE/ORDER STATUS CONFIGURATIONS
// ============================================================================
export const STATUS_LIST = [
  "order_received",
  "order_confirmed",
  "to_pick_up",
  "picked_up",
  "in_transit",
  "arrived_at_warehouse",
  "warehouse_processing",
  "to_deliver",
  "self_pickup_putaway",
  "delivering",
  "signed",
  "abnormal_signed",
  "return_collection",
];

export const STATUS_COLORS = {
  order_received: "bg-chart-1/10 text-chart-1 border-chart-1/20",
  order_confirmed: "bg-chart-4/10 text-chart-4 border-chart-4/20",
  to_pick_up: "bg-chart-5/10 text-chart-5 border-chart-5/20",
  picked_up: "bg-chart-3/10 text-chart-3 border-chart-3/20",
  in_transit: "bg-chart-4/10 text-chart-4 border-chart-4/20",
  arrived_at_warehouse: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  warehouse_processing: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  to_deliver: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  self_pickup_putaway: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  delivering: "bg-chart-4/10 text-chart-4 border-chart-4/20",
  signed: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  abnormal_signed: "bg-chart-4/10 text-chart-4 border-chart-4/20",
  return_collection: "bg-destructive/10 text-destructive border-destructive/20",
};

export const CHART_STATUS_COLORS = {
  order_received: "fill-chart-1",
  order_confirmed: "fill-chart-4",
  to_pick_up: "fill-chart-5",
  picked_up: "fill-chart-3",
  in_transit: "fill-chart-4",
  arrived_at_warehouse: "fill-chart-2",
  warehouse_processing: "fill-chart-2",
  to_deliver: "fill-chart-2",
  self_pickup_putaway: "fill-chart-2",
  delivering: "fill-chart-4",
  signed: "fill-chart-2",
  abnormal_signed: "fill-chart-4",
  return_collection: "fill-destructive",
};

export const STATUS_OPTIONS = {
  order_received: "status.order_received",
  order_confirmed: "status.order_confirmed",
  to_pick_up: "status.to_pick_up",
  picked_up: "status.picked_up",
  in_transit: "status.in_transit",
  arrived_at_warehouse: "status.arrived_at_warehouse",
  warehouse_processing: "status.warehouse_processing",
  to_deliver: "status.to_deliver",
  self_pickup_putaway: "status.self_pickup_putaway",
  delivering: "status.delivering",
  signed: "status.signed",
  abnormal_signed: "status.abnormal_signed",
  return_collection: "status.return_collection",
};

// ============================================================================
// BANKING STATUS CONFIGURATIONS
// ============================================================================
export const BANKING_STATUS = {
  VALIDATED: "validated",
  PENDING: "pending",
  REJECTED: "rejected",
  NOT_SET: "not_set",
};

export const BANKING_STATUS_COLORS = {
  validated: "bg-green-100 text-green-800 border-green-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  not_set: "bg-gray-100 text-gray-600 border-gray-200",
};

// ============================================================================
// DEFAULT VALUES AND CONFIGURATIONS
// ============================================================================
export const DEFAULT_VALUES = {
  // Pricing and Fees
  DELIVERY_FEES: 30,
  REFUSAL_FEES: 0,
  CANCELLATION_FEES: 0,
  DEADLINE: "24h - 48h",

  // Stock Management
  LOW_STOCK_THRESHOLD: 10,
  CRITICAL_STOCK_THRESHOLD: 5,
  REFRESH_INTERVAL_SECONDS: 30,

  // Form Validation
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 100,
  MIN_CNI_LENGTH: 4,
  MAX_CNI_LENGTH: 15,
  MIN_RIB_LENGTH: 24,
  MAX_RIB_LENGTH: 24,
  MIN_BANK_NAME_LENGTH: 2,
  MAX_BANK_NAME_LENGTH: 100,

  // File Upload
  MAX_FILE_SIZE_MB: 100,
  MAX_FILE_SIZE_BYTES: 100 * 1024 * 1024,
};

// ============================================================================
// TIME INTERVALS AND DURATIONS
// ============================================================================
export const TIME_INTERVALS = {
  REFRESH_INTERVALS: [
    { value: 15, label: "15 seconds" },
    { value: 30, label: "30 seconds" },
    { value: 60, label: "1 minute" },
    { value: 300, label: "5 minutes" },
  ],

  PAGINATION_OPTIONS: [
    { value: 10, label: "10 per page" },
    { value: 20, label: "20 per page" },
    { value: 50, label: "50 per page" },
    { value: 100, label: "100 per page" },
  ],
};

// ============================================================================
// FILE TYPES AND VALIDATIONS
// ============================================================================
export const ACCEPTED_FILE_TYPES = {
  IMAGES: ["image/jpeg", "image/jpg", "image/png"],
  DOCUMENTS: ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
  ALL: null, // null means accept all file types
  CSV: ["text/csv", "application/vnd.ms-excel"],
};

export const FILE_VALIDATION_MESSAGES = {
  SIZE_ERROR: "File size must be less than 10MB",
  TYPE_ERROR_IMAGES: "Only JPEG, JPG and PNG files are supported",
  TYPE_ERROR_DOCUMENTS: "Only JPEG, JPG, PNG and PDF files are supported",
  TYPE_ERROR_ALL: "All file types are accepted up to 10MB",
  REQUIRED: "File is required",
};

// ============================================================================
// FORM VALIDATION MESSAGES
// ============================================================================
export const VALIDATION_MESSAGES = {
  REQUIRED: "This field is required",
  EMAIL_INVALID: "Please enter a valid email address",
  PASSWORD_TOO_SHORT: "Password must be at least 8 characters",
  PASSWORD_TOO_LONG: "Password must not exceed 100 characters",
  CNI_TOO_SHORT: "CNI must be at least 8 characters",
  CNI_TOO_LONG: "CNI must not exceed 20 characters",
  RIB_TOO_SHORT: "RIB must be at least 24 characters",
  RIB_TOO_LONG: "RIB must not exceed 24 characters",
  BANK_NAME_TOO_SHORT: "Bank name must be at least 2 characters",
  BANK_NAME_TOO_LONG: "Bank name must not exceed 100 characters",
  POSITIVE_NUMBER: "Value must be a positive number",
  NON_NEGATIVE: "Value must be non-negative",
};

// ============================================================================
// API RETRY CONFIGURATIONS
// ============================================================================
export const API_CONFIG = {
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 1000,
  MAX_RETRY_DELAY_MS: 30000,
  REQUEST_TIMEOUT_MS: 10000,
};
